import { HTMLAttributes, ReactNode } from "react";
import Icon from "../../../base/components/Icon";
import { cx } from "../../utils/cx";
import styles from "./Callout.module.scss";

export type CalloutVariant = "error" | "warning" | "info" | "success";

export type CalloutWidth = "fit" | "full";

export type CalloutProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
    /** Visual + semantic tone. Drives background, text color, and default icon. */
    variant: CalloutVariant;
    /** Message body. Typically a short sentence or a `<p>`. */
    children: ReactNode;
    /**
     * Horizontal size. `full` spans the parent; `fit` hugs the message.
     * Defaults to `full`.
     */
    width?: CalloutWidth;
    /**
     * Optional override for the leading `Icon` name.
     * Defaults to the icon mapped from `variant`.
     */
    icon?: string;
    /**
     * Accessibility role. Defaults to `alert` for `error`, `status` otherwise.
     */
    role?: "alert" | "status";
};

const ICON_BY_VARIANT: Record<CalloutVariant, string> = {
    error: "error",
    warning: "warning",
    info: "info",
    success: "status-success",
};

/**
 * Treez DS — Callout (inline status alert).
 *
 * Icon + message row for contextual feedback inside a page or pane.
 * Use `width="full"` for page banners and `width="fit"` for compact notices.
 * Distinct from **Message** (empty / error state) and **MessageBanner** (dark bar).
 */
export function Callout({
    variant,
    children,
    className,
    width = "full",
    icon,
    role,
    ...rest
}: CalloutProps) {
    const resolvedRole = role ?? (variant === "error" ? "alert" : "status");

    return (
        <div
            className={cx(styles.root, className)}
            data-ui="callout"
            data-variant={variant}
            data-width={width}
            role={resolvedRole}
            {...rest}
        >
            <span aria-hidden className={styles.icon}>
                <Icon icon={icon || ICON_BY_VARIANT[variant]} />
            </span>
            <div className={styles.content}>{children}</div>
        </div>
    );
}
